/**
 * Procedural manga page layout — an IRREGULAR GRID WITH DIAGONALS and a UNIFORM GAP.
 *
 * The page is recursively split (BSP) by straight, possibly-slanted lines. Crucially
 * every cut is a SHARED edge: both children use the exact same line, so panels tile
 * the page with no overlaps. Splits are quads throughout. Only at the end do we inset
 * each quad inward by a fixed distance (half-gutter) on every edge — that produces the
 * uniform gap between every panel and at the page margin. Slanted cuts stay clean
 * because neighbours share the line before insetting.
 *
 * Panels come out in the project's reading order: rows top→bottom, and within a
 * row right→left for manga (RTL) or left→right for comics/HQ (LTR).
 */
export type ReadingDirection = 'rtl' | 'ltr';
export interface TplPoint { x: number; y: number }
type Quad = [TplPoint, TplPoint, TplPoint, TplPoint]; // clockwise: TL, TR, BR, BL

const GUTTER = 0.011;  // half-gutter — the uniform gap (and page margin) is 2×GUTTER
const MAX_SLANT = 0.11;

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const lerp = (a: TplPoint, b: TplPoint, t: number): TplPoint => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const dist = (a: TplPoint, b: TplPoint) => Math.hypot(b.x - a.x, b.y - a.y);

/** Split a quad into `count` quads, sharing exact cut lines, in reading order.
 * `rtl` controls which HORIZONTAL side reads first (and so gets the first `a`
 * panels): the RIGHT for manga, the LEFT for comics/HQ. Vertical splits read
 * top→bottom regardless of direction. */
function splitQuad(q: Quad, count: number, rtl: boolean): Quad[] {
  if (count <= 1) return [q];
  const a = Math.max(1, Math.min(count - 1, Math.round(count / 2 + rnd(-0.5, 0.5))));
  const b = count - a;

  const w = (dist(q[0], q[1]) + dist(q[3], q[2])) / 2; // avg width
  const h = (dist(q[0], q[3]) + dist(q[1], q[2])) / 2; // avg height
  const vertical = w > h * 1.12 ? true : h > w * 1.12 ? false : Math.random() < 0.5;
  const s = rnd(-MAX_SLANT, MAX_SLANT);

  if (vertical) {
    // The first-read side gets `a` panels → its width share.
    const share = Math.min(0.72, Math.max(0.28, a / count + rnd(-0.1, 0.1)));
    if (rtl) {
      const f = 1 - share; // cut from the left → right side width = share (reads first)
      const topPt = lerp(q[0], q[1], f + s);
      const botPt = lerp(q[3], q[2], f - s);
      const right: Quad = [topPt, q[1], q[2], botPt];
      const left: Quad = [q[0], topPt, botPt, q[3]];
      return [...splitQuad(right, a, rtl), ...splitQuad(left, b, rtl)];
    }
    const f = share; // cut from the left → left side width = share (reads first)
    const topPt = lerp(q[0], q[1], f + s);
    const botPt = lerp(q[3], q[2], f - s);
    const right: Quad = [topPt, q[1], q[2], botPt];
    const left: Quad = [q[0], topPt, botPt, q[3]];
    return [...splitQuad(left, a, rtl), ...splitQuad(right, b, rtl)];
  }
  const share = Math.min(0.72, Math.max(0.28, a / count + rnd(-0.1, 0.1)));
  const leftPt = lerp(q[0], q[3], share + s);   // top side reads first
  const rightPt = lerp(q[1], q[2], share - s);
  const top: Quad = [q[0], q[1], rightPt, leftPt];
  const bottom: Quad = [leftPt, rightPt, q[2], q[3]];
  return [...splitQuad(top, a, rtl), ...splitQuad(bottom, b, rtl)];
}

/** Uniformly inset a convex quad inward by `d` on every edge (the gutter). */
function insetQuad(q: Quad, d: number): TplPoint[] {
  const c = centroid(q);
  const n = q.length;
  // Each edge → an inward-offset line (point + direction).
  const lines = q.map((p, i) => {
    const a = p, b = q[(i + 1) % n];
    const ex = b.x - a.x, ey = b.y - a.y;
    const L = Math.hypot(ex, ey) || 1;
    // pick the perpendicular that points toward the centroid (inward)
    let nx = -ey / L, ny = ex / L;
    if ((c.x - (a.x + b.x) / 2) * nx + (c.y - (a.y + b.y) / 2) * ny < 0) { nx = -nx; ny = -ny; }
    return { px: a.x + nx * d, py: a.y + ny * d, dx: ex, dy: ey };
  });
  // New vertex[i] = intersection of offset edge (i-1) and edge (i).
  const out: TplPoint[] = [];
  for (let i = 0; i < n; i++) {
    const l1 = lines[(i - 1 + n) % n], l2 = lines[i];
    const den = l1.dx * l2.dy - l1.dy * l2.dx;
    if (Math.abs(den) < 1e-9) { out.push(q[i]); continue; }
    const t = ((l2.px - l1.px) * l2.dy - (l2.py - l1.py) * l2.dx) / den;
    out.push({ x: l1.px + l1.dx * t, y: l1.py + l1.dy * t });
  }
  return out.map((p) => ({ x: Math.max(0, Math.min(1, p.x)), y: Math.max(0, Math.min(1, p.y)) }));
}

export function centroid(pts: TplPoint[]): TplPoint {
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return { x: x / pts.length, y: y / pts.length };
}

/** Generate an irregular-grid layout with `n` panels, uniform gaps, ordered in
 * the project's reading direction (RTL manga by default, LTR for comics/HQ). */
export function generateMangaLayout(n: number, direction: ReadingDirection = 'rtl'): TplPoint[][] {
  const rtl = direction === 'rtl';
  const count = Math.max(1, Math.min(12, Math.round(n)));
  // Page starts a half-gutter in; each panel is inset another half-gutter → the
  // outer page margin equals the inner gap (2×GUTTER) exactly. Uniform everywhere.
  const m = GUTTER;
  const page: Quad = [
    { x: m, y: m }, { x: 1 - m, y: m }, { x: 1 - m, y: 1 - m }, { x: m, y: 1 - m },
  ];
  const cells = count === 1 ? [page] : splitQuad(page, count, rtl);
  let panels = cells.map((q) => insetQuad(q, GUTTER));

  // ROUNDED WALLS — some pages get panels with rounded corners (soft/flashback
  // mood, very common in manga). Implemented as POLYLINE approximations of the
  // corner arcs, so every consumer (CSS polygon clip, exports, guided-reader
  // veil) keeps working on plain point lists with zero special-casing.
  if (Math.random() < 0.4) {
    const roundAll = Math.random() < 0.5; // whole page soft vs. a few panels
    panels = panels.map((pts) => {
      if (!roundAll && Math.random() > 0.45) return pts;
      return roundPolygonCorners(pts, 0.035 + Math.random() * 0.03);
    });
  }

  // Reading order: band by top edge (rows), then within a row right→left for
  // manga (RTL) or left→right for comics/HQ (LTR).
  const BAND = 0.14;
  panels.sort((A, B) => {
    const rowA = Math.floor(Math.min(...A.map((p) => p.y)) / BAND);
    const rowB = Math.floor(Math.min(...B.map((p) => p.y)) / BAND);
    if (rowA !== rowB) return rowA - rowB;
    return rtl ? centroid(B).x - centroid(A).x : centroid(A).x - centroid(B).x;
  });
  return panels;
}

/** Replaces each corner with a small quadratic-arc POLYLINE (3 interior points),
 * clamped to at most 40% of each adjacent edge so shapes never self-intersect. */
function roundPolygonCorners(pts: TplPoint[], radius: number): TplPoint[] {
  const n = pts.length;
  if (n < 3) return pts;
  const out: TplPoint[] = [];
  for (let i = 0; i < n; i++) {
    const prev = pts[(i - 1 + n) % n], cur = pts[i], next = pts[(i + 1) % n];
    const lenIn = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const lenOut = Math.hypot(next.x - cur.x, next.y - cur.y);
    const r = Math.min(radius, lenIn * 0.4, lenOut * 0.4);
    if (r < 0.004) { out.push(cur); continue; }
    // entry/exit points of the arc on each edge
    const pIn = { x: cur.x + ((prev.x - cur.x) / lenIn) * r, y: cur.y + ((prev.y - cur.y) / lenIn) * r };
    const pOut = { x: cur.x + ((next.x - cur.x) / lenOut) * r, y: cur.y + ((next.y - cur.y) / lenOut) * r };
    // quadratic bezier pIn→(ctrl=cur)→pOut sampled at t=.25/.5/.75
    for (const t of [0, 0.25, 0.5, 0.75, 1]) {
      const u = 1 - t;
      out.push({
        x: u * u * pIn.x + 2 * u * t * cur.x + t * t * pOut.x,
        y: u * u * pIn.y + 2 * u * t * cur.y + t * t * pOut.y,
      });
    }
  }
  return out;
}

export function toPolygonCss(pts: TplPoint[]): string {
  return `polygon(${pts.map((p) => `${(p.x * 100).toFixed(2)}% ${(p.y * 100).toFixed(2)}%`).join(', ')})`;
}
